import { useState, useEffect } from 'react';
import { Copy, Check, UserPlus, KeyRound, Wallet, Gamepad2, ArrowRight, Zap, ExternalLink, Globe, Gift } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const HowToClaim = () => {
  const [copied, setCopied] = useState(false);
  const promoCode = 'CS2026';

  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Can CSGOLuck code be claimed in different ways?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CSGOLuck code can be claimed in two ways. Players can do so by clicking on the \"REWARDS\" tab or by navigating to the deposit section on the website."
          }
        },
        {
          "@type": "Question",
          "name": "Is it possible for existing players to redeem the CSGO Luck bonus?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Just like newcomers, existing players can also claim the CSGO Luck bonus. It is important that such accounts have not used a welcome bonus offer on the site before."
          }
        },
        {
          "@type": "Question",
          "name": "Is it mandatory to use a referral code to get the welcome bonus?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No, using the referral code is just a common option. New players can redeem the welcome bonus by signing up for an account through the referral link."
          }
        },
        {
          "@type": "Question",
          "name": "What payment methods can be used to claim the code?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "All payment methods on CSGOLuck are eligible for code redemption. This includes real money, CS2 skins, gift cards, and cryptocurrencies."
          }
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(faqSchema);
    script.id = 'how-to-claim-faq-schema';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('how-to-claim-faq-schema');
      if (existingScript) existingScript.remove();
    };
  }, []);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(promoCode);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = promoCode;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      toast.success('Promo code copied!');
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast.error('Failed to copy code');
    }
  };

  const steps = [
    {
      number: '01',
      icon: <Globe className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Visit CSGOLuck Website',
      description: 'Visit CSGOLuck or click on the referral link that will redirect you to the platform directly.',
      tip: null,
    },
    {
      number: '02',
      icon: <UserPlus className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Create Account',
      description: 'Press the "Sign In" button located in the top right corner of the website. Register with Steam or Google account.',
      tip: 'Register with Steam if you want to easily deposit and withdraw CS2 skins.',
    },
    {
      number: '03',
      icon: <Gift className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Click on the "REWARDS" Section',
      description: 'The section is located in the main navigational menu, accompanied by the present symbol.',
      tip: 'The code can be activated through the deposit section as well.',
    },
    {
      number: '04',
      icon: <KeyRound className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Enter CSGOLuck Promo Code',
      description: 'In the text bar called "Referral or Promo or Gift Code", enter the code CS2026. Press the green "Confirm" button afterward.',
      tip: null,
    },
    {
      number: '05',
      icon: <Wallet className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Make a Deposit',
      description: 'All deposit methods are eligible for the deposit bonus. The maximum deposit bonus is 100 coins.',
      tip: 'Deposit $70 to get the maximum bonus amount.',
    },
    {
      number: '06',
      icon: <Gamepad2 className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: 'Start Playing',
      description: 'Bonus funds are added to the account as soon as the deposit is processed. Bonus funds can be used across all games on CSGOLuck.',
      tip: 'Bonus funds have 40x wagering requirements before withdrawal can be made.',
    },
  ];

  return (
    <div className="min-h-screen bg-csgo-bg pt-20 sm:pt-24 md:pt-32 pb-16">
      <div className="fixed inset-0 bg-grid opacity-20 pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12" data-testid="how-to-header">
          <h1 className="font-orbitron font-black text-2xl sm:text-4xl md:text-5xl text-white mb-3 sm:mb-4">
            How to Claim <span className="text-csgo-primary neon-text">CSGOLuck</span> Bonus Code
          </h1>
          <p className="font-rajdhani text-sm sm:text-lg text-csgo-textMuted max-w-xl mx-auto">
            Follow the steps below to get your 100% deposit bonus up to 100 coins
          </p>
        </div>

        {/* Promo Code Quick Copy */}
        <div className="bg-csgo-surface/80 backdrop-blur-md border border-csgo-primary/30 rounded-xl p-4 sm:p-6 mb-8 sm:mb-12 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4" data-testid="quick-copy">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-csgo-primary/20 rounded-xl flex items-center justify-center">
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-csgo-primary" />
            </div>
            <div>
              <p className="font-rajdhani text-csgo-textMuted text-xs sm:text-sm">Your Promo Code</p>
              <p className="font-orbitron font-bold text-xl sm:text-2xl text-csgo-primary">{promoCode}</p>
            </div>
          </div>
          <button
            onClick={handleCopy}
            data-testid="quick-copy-btn"
            className={`flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-rajdhani font-semibold transition-all duration-200 w-full sm:w-auto justify-center ${
              copied 
                ? 'bg-csgo-primary text-black' 
                : 'bg-csgo-bg border border-csgo-border text-white hover:border-csgo-primary'
            }`}
          >
            {copied ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : <Copy className="w-4 h-4 sm:w-5 sm:h-5" />}
            {copied ? 'Copied!' : 'Copy Code'}
          </button>
        </div>

        {/* Steps */}
        <div className="space-y-4 sm:space-y-6 mb-8 sm:mb-12" data-testid="steps-section">
          {steps.map((step, index) => (
            <div 
              key={index}
              className="bg-csgo-surface/60 backdrop-blur-sm border border-csgo-border rounded-xl p-4 sm:p-6 md:p-8 card-hover group"
              data-testid={`step-${index + 1}`}
            >
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                {/* Step Number */}
                <div className="flex-shrink-0 flex sm:block items-center gap-3 sm:gap-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-csgo-bg border border-csgo-primary/30 rounded-xl flex items-center justify-center group-hover:border-csgo-primary transition-colors">
                    <span className="font-orbitron font-black text-lg sm:text-2xl text-csgo-primary">{step.number}</span>
                  </div>
                  {/* Mobile: show title next to number */}
                  <div className="sm:hidden flex items-center gap-2">
                    <div className="w-8 h-8 bg-csgo-primary/10 rounded-lg flex items-center justify-center text-csgo-primary">
                      {step.icon}
                    </div>
                    <h2 className="font-orbitron font-bold text-sm text-white">{step.title}</h2>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-grow">
                  <div className="hidden sm:flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-csgo-primary/10 rounded-lg flex items-center justify-center text-csgo-primary">
                      {step.icon}
                    </div>
                    <h2 className="font-orbitron font-bold text-xl text-white">{step.title}</h2>
                  </div>
                  <p className="font-rajdhani text-sm sm:text-lg text-csgo-textMuted mb-3 sm:mb-4">{step.description}</p>
                  {step.tip && (
                    <div className="bg-csgo-bg/50 rounded-lg px-3 sm:px-4 py-2 sm:py-3 border-l-2 border-csgo-secondary">
                      <p className="font-rajdhani text-xs sm:text-sm text-csgo-secondary">
                        <span className="font-semibold">Pro Tip:</span> {step.tip}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-csgo-primary/10 to-csgo-secondary/10 border border-csgo-primary/30 rounded-2xl p-6 sm:p-8 mb-8 sm:mb-12 text-center" data-testid="how-to-cta">
          <h2 className="font-orbitron font-bold text-xl sm:text-2xl text-white mb-3 sm:mb-4">
            Ready to Get Started on <span className="text-csgo-primary">CSGOLuck</span> Instantly?
          </h2>
          <p className="font-rajdhani text-sm sm:text-base text-csgo-textMuted mb-5 sm:mb-6 max-w-lg mx-auto">
            Click the button below to visit the CSGOLuck website and to apply the promo code automatically.
          </p>
          <a
            href="https://csgoluck.com/r/CS2026"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="how-to-claim-btn"
            className="inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-3 sm:py-4 bg-csgo-primary text-black font-orbitron font-bold text-sm sm:text-lg uppercase tracking-wider rounded-xl hover:bg-csgo-primary/90 transition-all duration-200 neon-glow hover:scale-105 w-full sm:w-auto"
          >
            Claim Your Bonus
            <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
          </a>
        </div>

        {/* FAQs */}
        <div data-testid="faq-section">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="font-orbitron font-bold text-xl sm:text-2xl md:text-3xl text-white mb-2">
              Frequently Asked <span className="text-csgo-primary">Questions</span>
            </h2>
            <p className="font-rajdhani text-sm sm:text-base text-csgo-textMuted">
              All you need to know about the activation of CSGOLuck promo codes.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
            {[
              {
                question: 'Can CSGOLuck code be claimed in different ways?',
                answer: 'CSGOLuck code can be claimed in two ways. Players can do so by clicking on the "REWARDS" tab or by navigating to the deposit section on the website.'
              },
              {
                question: 'Is it possible for existing players to redeem the CSGO Luck bonus?',
                answer: 'Just like newcomers, existing players can also claim the CSGO Luck bonus. It is important that such accounts have not used a welcome bonus offer on the site before.'
              },
              {
                question: 'Is it mandatory to use a referral code to get the welcome bonus?',
                answer: 'No, using the referral code is just a common option. New players can redeem the welcome bonus by signing up for an account through the referral link.'
              },
              {
                question: 'What payment methods can be used to claim the code?',
                answer: 'All payment methods on CSGOLuck are eligible for code redemption. This includes real money, CS2 skins, gift cards, and cryptocurrencies.'
              },
            ].map((faq, index) => (
              <div 
                key={index}
                className="bg-csgo-surface/60 border border-csgo-border rounded-xl p-4 sm:p-6"
                data-testid={`faq-${index}`}
              >
                <h3 className="font-orbitron font-bold text-sm sm:text-base text-white mb-2">{faq.question}</h3>
                <p className="font-rajdhani text-xs sm:text-sm text-csgo-textMuted">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 sm:mt-12 text-center">
          <p className="font-rajdhani text-xs sm:text-sm text-csgo-textMuted">
            This is an affiliate site. Gamble responsibly. 18+ only. 
            <br />Terms and conditions apply on CSGO Luck.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HowToClaim;
